import instance, { isErrorHandled } from 'api';
import Button from 'components/button/Button';
import { endpoints } from 'endpoints.config';
import { Form, Formik, FormikHelpers } from 'formik';
import React, { useState } from 'react';
import * as Yup from 'yup';
import FormTextField from 'components/form/FormTextField';
import { toCamelCase } from 'helpers/formatFormFieldNames';
import { getErrorMessage } from 'errors';
import { Toast, ToastMessageReason } from 'helpers/toast';
import { useNavigate } from '@reach/router';
import { CardSubPage } from './CardPage';
import { Modal } from 'components/modal/Modal';
import { TFAField } from 'components/form/TFAField';
import { useTFAField } from 'helpers/useTFAField';
import { useTranslation } from 'react-i18next';

const initialValues = {
    oldPin: '',
    newPin: '',
};

const validationSchema = Yup.object({
    oldPin: Yup.string().required('Please enter your current PIN'),
    newPin: Yup.string().required('Please enter a new PIN').length(4, 'PIN must be 4 digits long'),
});

const tfaValidationSchema = Yup.object({
    tfaCode: Yup.string().required('Please enter your two-factor authentication code'),
});

export const ChangePin = ({ cardId }: { cardId: number }) => {
    const [errorMessage, setErrorMessage] = useState('');
    const [pinValues, setPinValues] = useState<typeof initialValues | null>(null);
    const [showTfaModal, setShowTfaModal] = useState(false);
    const navigate = useNavigate();
    const [tfaType, toggleTfaType, reSendTfaCode] = useTFAField();
    const { t } = useTranslation('common');

    const handlePinSubmit = (
        values: typeof initialValues,
        formikHelpers: FormikHelpers<typeof initialValues>
    ) => {
        setPinValues(values);
        setShowTfaModal(true);
        formikHelpers.setSubmitting(false);
    };

    const handleTfaSubmit = (
        { tfaCode }: { tfaCode: string },
        formikHelpers: FormikHelpers<{ tfaCode: string }>
    ) => {
        if (!pinValues) return;

        instance
            .post(endpoints.cards.changeCardPin, {
                ...pinValues,
                topUpCardsId: cardId,
                tfaCode,
                tfaType,
            })
            .then((res) => {
                Toast.openToastMessage('PIN Changed Successfully', ToastMessageReason.VALID);
                setShowTfaModal(false);
                navigate(`../${cardId}/${CardSubPage.TRANSACTIONS}`);
            })
            .catch((err) => {
                formikHelpers.setSubmitting(false);
                if (isErrorHandled(err)) {
                    const errors = err.response.data.errors;
                    const tfaError = errors.find(
                        (error) => toCamelCase(error.fieldName) === 'tfaCode'
                    );
                    if (tfaError) {
                        formikHelpers.setFieldError('tfaCode', getErrorMessage(tfaError.messageCode));
                    } else if (errors.some((error) => ['OldPin', 'NewPin'].includes(error.fieldName))) {
                        setShowTfaModal(false);
                        setErrorMessage(getErrorMessage(errors[0].messageCode));
                    } else {
                        setErrorMessage(getErrorMessage(errors[0].messageCode));
                    }
                } else {
                    setErrorMessage('Something went wrong');
                }
            });
    };

    const closeTfaModal = () => {
        setShowTfaModal(false);
        setPinValues(null);
    };

    return (
        <div className="ChangePinPage FormPage">
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handlePinSubmit}
            >
                {(props) => (
                    <Form>
                        <FormTextField
                            field="oldPin"
                            required
                            label="Current PIN"
                            placeholder="4 digit pin"
                            inputMode="numeric"
                            type="password"
                            maxLength={4}
                            onChange={(e) => {
                                props.setFieldValue('oldPin', e.target.value.replaceAll(/\D/g, ''));
                            }}
                        />
                        <FormTextField
                            field="newPin"
                            required
                            label="New PIN"
                            placeholder="4 digit pin"
                            inputMode="numeric"
                            type="password"
                            maxLength={4}
                            onChange={(e) => {
                                props.setFieldValue('newPin', e.target.value.replaceAll(/\D/g, ''));
                            }}
                        />
                        <Button variety="full" type="submit" disabled={props.isSubmitting}>
                            {t('changePin')}
                        </Button>
                    </Form>
                )}
            </Formik>
            {errorMessage && <div className="ErrorLabel">{errorMessage}</div>}

            {showTfaModal && (
                <Formik
                    initialValues={{ tfaCode: '' }}
                    validationSchema={tfaValidationSchema}
                    onSubmit={handleTfaSubmit}
                >
                    {({ isValid, isSubmitting }) => (
                        <Form>
                            <Modal
                                title={t('changePin')}
                                onCloseModal={closeTfaModal}
                                customButtons={
                                    <div className="ModalNavigation">
                                        <Button priority="secondary" type="button" onClick={closeTfaModal}>
                                            {t('cancel')}
                                        </Button>
                                        <Button
                                            priority="primary"
                                            type="submit"
                                            color="third"
                                            disabled={isSubmitting || !isValid}
                                        >
                                            {t('confirm')}
                                        </Button>
                                    </div>
                                }
                            >
                                <>
                                    <TFAField
                                        field="tfaCode"
                                        label={'TFA code'}
                                        required={true}
                                        toggleTFAType={toggleTfaType}
                                        tfaType={tfaType}
                                        handleResendCode={reSendTfaCode}
                                        autoFocus
                                        holdFocus
                                        onClose={closeTfaModal}
                                        showTimer={true}
                                    />
                                </>
                            </Modal>
                        </Form>
                    )}
                </Formik>
            )}
        </div>
    );
};
